import React, { useState } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { useLanguage } from '../i18n/LanguageContext';
import { withTxToast } from '../utils/txToast';
import { Send, Loader, CheckCircle, AlertCircle } from 'lucide-react';

const TransferTokens = () => {
  const { web3, account, contract } = useWeb3();
  const { t } = useLanguage();
  const [recipient, setRecipient] = useState('');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [txHash, setTxHash] = useState('');
  const [error, setError] = useState('');

  const normalizeTxError = (err) => {
    const message =
      err?.reason ||
      err?.data?.originalError?.message ||
      err?.data?.message ||
      err?.data?.message ||
      err?.cause?.message ||
      err?.message ||
      t('transfer.toastError');

    if (String(message).includes('EnforcedPause')) {
      return 'El contrato BFPE esta pausado y no permite transferencias ahora mismo.';
    }

    if (String(message).includes('insufficient funds')) {
      return 'Fondos insuficientes para enviar la cantidad indicada.';
    }

    if (String(message).includes('Internal JSON-RPC error')) {
      return 'MetaMask rechazo la transaccion (JSON-RPC interno). Revisa permisos de la cuenta y configuracion de red en MetaMask.';
    }

    return message;
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    
    if (!contract || !account) {
      setError(t('transfer.errNoWallet'));
      return;
    }

    if (!web3.utils.isAddress(recipient)) {
      setError(t('transfer.errInvalidAddress'));
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      setError(t('transfer.errInvalidAmount'));
      return;
    }

    setLoading(true);
    setError('');
    setTxHash('');

    try {
      const amountWei = web3.utils.toWei(amount, 'ether');

      // Pre-checks to surface actionable errors before opening MetaMask.
      const [rawBalance, isPaused] = await Promise.all([
        contract.methods.balanceOf(account).call(),
        contract.methods.paused().call().catch(() => false),
      ]);

      if (isPaused) {
        throw new Error('El contrato BFPE esta pausado y no permite transferencias ahora mismo.');
      }

      if (BigInt(rawBalance) < BigInt(amountWei)) {
        throw new Error('Fondos insuficientes para enviar la cantidad indicada.');
      }

      // Preflight call catches revert reasons that MetaMask often wraps as generic JSON-RPC errors.
      await contract.methods.transfer(recipient, amountWei).call({ from: account, gasPrice: '0' });

      // Use one deterministic legacy tx path on BFPE to avoid duplicate attempts/timeouts.
      const tx = await withTxToast(
        () => contract.methods.transfer(recipient, amountWei).send({
          from: account,
          gas: 120000,
          gasPrice: '0',
          type: '0x0',
        }),
        t('transfer.toastLoading'),
        t('transfer.toastSuccess', { amount })
      );

      setTxHash(tx.transactionHash);
      setRecipient('');
      setAmount('');
    } catch (err) {
      setError(normalizeTxError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <div className="flex items-center space-x-2 mb-4">
        <Send className="w-6 h-6 text-purple-600" />
        <h2 className="text-2xl font-bold text-gray-800">{t('transfer.title')}</h2>
      </div>

      {!account ? (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            {t('transfer.noWallet')}
          </p>
        </div>
      ) : (
        <form onSubmit={handleTransfer} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('transfer.recipientLabel')}
            </label>
            <input
              type="text"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder={t('transfer.recipientPlaceholder')}
              className="input-field"
              disabled={loading}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('transfer.amountLabel')}
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={t('transfer.amountPlaceholder')}
              step="0.000000000000000001"
              min="0"
              className="input-field"
              disabled={loading}
              required
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {txHash && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-2">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-green-800 font-medium">{t('transfer.successTitle')}</p>
                <p className="text-xs text-green-700 mt-1 break-all">{t('transfer.successTx', { hash: txHash })}</p>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !account}
            className="btn-primary w-full flex items-center justify-center space-x-2"
          >
            {loading ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                <span>{t('transfer.processing')}</span>
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                <span>{t('transfer.submit')}</span>
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
};

export default TransferTokens;
